/**
 * Job logs component
 * Real-time log display for job execution
 */

import { useRef, useEffect, useState } from 'react';
import { Card, Typography, Space, Button, Tag, Input, Empty, Tooltip, Switch } from 'antd';
import {
  ClearOutlined,
  DownloadOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  BugOutlined,
  PauseOutlined,
  CaretRightOutlined,
} from '@ant-design/icons';
import { formatDate } from '@/utils/formatters';

const { Text, Paragraph } = Typography;

export type LogLevel = 'info' | 'warn' | 'error' | 'debug';

export interface LogEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  message: string;
  details?: Record<string, unknown>;
  cellId?: string;
}

interface JobLogsProps {
  logs: LogEntry[];
  maxHeight?: number | string;
  onClear?: () => void;
  onExport?: () => void;
  autoScroll?: boolean;
}

const levelConfig: Record<LogLevel, { icon: React.ReactNode; color: string; tag: string }> = {
  info: {
    icon: <InfoCircleOutlined />,
    color: '#1677ff',
    tag: 'blue',
  },
  warn: {
    icon: <WarningOutlined />,
    color: '#faad14',
    tag: 'gold',
  },
  error: {
    icon: <CloseCircleOutlined />,
    color: '#ff4d4f',
    tag: 'red',
  },
  debug: {
    icon: <BugOutlined />,
    color: '#8c8c8c',
    tag: 'default',
  },
};

function LogLine({ log }: { log: LogEntry }) {
  const config = levelConfig[log.level];
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        padding: '4px 0',
        borderBottom: '1px solid var(--color-border)',
        fontFamily: 'monospace',
        fontSize: 12,
      }}
    >
      {/* Timestamp */}
      <Text type="secondary" style={{ flexShrink: 0, width: 85 }}>
        {formatDate(log.timestamp, 'time')}
      </Text>

      {/* Level tag */}
      <Tag color={config.tag} style={{ margin: 0, flexShrink: 0 }}>
        {log.level.toUpperCase()}
      </Tag>

      {/* Cell ID if present */}
      {log.cellId && (
        <Tag style={{ margin: 0, flexShrink: 0 }}>
          Cell {log.cellId}
        </Tag>
      )}

      {/* Message */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{ color: config.color, wordBreak: 'break-word' }}
          onClick={() => log.details && setExpanded(!expanded)}
        >
          {log.message}
        </Text>

        {/* Expandable details */}
        {log.details && expanded && (
          <pre
            style={{
              margin: '4px 0 0',
              padding: 8,
              background: 'var(--color-bg-spotlight)',
              borderRadius: 4,
              fontSize: 11,
              overflow: 'auto',
              maxHeight: 200,
            }}
          >
            {JSON.stringify(log.details, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

export function JobLogs({
  logs,
  maxHeight = 400,
  onClear,
  onExport,
  autoScroll: initialAutoScroll = true,
}: JobLogsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState<LogLevel | 'all'>('all');
  const [autoScroll, setAutoScroll] = useState(initialAutoScroll);
  const [paused, setPaused] = useState(false);

  // Auto-scroll to bottom
  useEffect(() => {
    if (autoScroll && !paused && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs, autoScroll, paused]);

  // Filter logs
  const filteredLogs = logs.filter((log) => {
    if (levelFilter !== 'all' && log.level !== levelFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        log.message.toLowerCase().includes(query) ||
        log.cellId?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const handleExport = () => {
    if (onExport) {
      onExport();
      return;
    }

    // Default export as text file
    const content = filteredLogs
      .map(
        (log) =>
          `[${log.timestamp}] [${log.level.toUpperCase()}]${log.cellId ? ` [Cell ${log.cellId}]` : ''} ${log.message}`
      )
      .join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `job-logs-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card
      title="Execution Logs"
      extra={
        <Space size={8}>
          <Input
            placeholder="Search..."
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: 150 }}
            size="small"
            allowClear
          />

          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value as LogLevel | 'all')}
            style={{
              padding: '2px 8px',
              borderRadius: 4,
              border: '1px solid var(--color-border)',
              fontSize: 12,
            }}
          >
            <option value="all">All Levels</option>
            <option value="info">Info</option>
            <option value="warn">Warning</option>
            <option value="error">Error</option>
            <option value="debug">Debug</option>
          </select>

          <Tooltip title={paused ? 'Resume auto-scroll' : 'Pause auto-scroll'}>
            <Button
              size="small"
              icon={paused ? <CaretRightOutlined /> : <PauseOutlined />}
              onClick={() => setPaused(!paused)}
            />
          </Tooltip>

          <Tooltip title="Export logs">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={handleExport}
            />
          </Tooltip>

          {onClear && (
            <Tooltip title="Clear logs">
              <Button
                size="small"
                icon={<ClearOutlined />}
                onClick={onClear}
              />
            </Tooltip>
          )}
        </Space>
      }
    >
      <div
        ref={containerRef}
        style={{
          maxHeight,
          overflowY: 'auto',
          fontFamily: 'monospace',
        }}
      >
        {filteredLogs.length === 0 ? (
          <Empty
            description={
              logs.length === 0
                ? 'No logs yet'
                : 'No logs match the current filter'
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          filteredLogs.map((log) => <LogLine key={log.id} log={log} />)
        )}
      </div>

      {/* Stats footer */}
      <div
        style={{
          marginTop: 12,
          paddingTop: 8,
          borderTop: '1px solid var(--color-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          {filteredLogs.length} of {logs.length} entries
        </Text>
        <Space size={4}>
          <Switch
            size="small"
            checked={autoScroll}
            onChange={setAutoScroll}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Auto-scroll
          </Text>
        </Space>
      </div>
    </Card>
  );
}

export default JobLogs;
